/**
 * E-Notes — Module unifié de production de documents médicaux
 * 4 onglets : Feuille de soins | ALD | Médicaments | Dentaire
 * Pattern : NGAP auto-chargé par profil médecin + PPV Maroc
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  FileSignature, HeartHandshake, Pill, Sparkles, Plus, Trash2, Save, Printer,
  FileText, Send, CheckCircle2, AlertCircle, Stethoscope, User, Calendar, Hash,
  CreditCard, MapPin, Building2, Settings,
} from "lucide-react";
import { MOCK_DOCTORS, getActsForDoctor, getDoctorCotation, SPECIALTY_LABEL, MOCK_COTATIONS } from "@/lib/referential/doctor-cotations";
import { MEDICATIONS } from "@/lib/referential/medications";
import { formatAmountMad, type NgapAct } from "@/lib/ngap-catalogue";
import { PPV_TABLE, getMedicationsForSpecialty } from "@/lib/referential/medication-rules";

import { ALD_ENTRIES } from "@/lib/referential/ald";
import { ICD10_ENTRIES } from "@/lib/referential/cim10";

export const Route = createFileRoute("/e-notes/")({
  component: ENotesModule,
});

function ENotesModule() {
  const [activeTab, setActiveTab] = useState("fse");
  const [selectedDoctorId, setSelectedDoctorId] = useState(MOCK_DOCTORS[0].id);
  const selectedDoctor = MOCK_DOCTORS.find((d) => d.id === selectedDoctorId)!;
  const cotation = getDoctorCotation(selectedDoctorId);
  // The allowed acts come from the canonical catalogue, loaded lazily.
  const [allowedActs, setAllowedActs] = useState<Awaited<ReturnType<typeof getActsForDoctor>>>([]);
  useEffect(() => {
    let cancelled = false;
    getActsForDoctor(selectedDoctorId)
      .then((loaded) => { if (!cancelled) setAllowedActs(loaded); })
      .catch(() => { if (!cancelled) setAllowedActs([]); });
    return () => { cancelled = true; };
  }, [selectedDoctorId]);

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <FileSignature className="h-5 w-5" /> E-Notes
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Notes cliniques numériques et comptes-rendus · NGAP, ALD, Médicaments, Dentaire
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Médecin :</span>
          <Select value={selectedDoctorId} onValueChange={setSelectedDoctorId}>
            <SelectTrigger className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MOCK_DOCTORS.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name} <span className="text-muted-foreground ml-1">· {SPECIALTY_LABEL[d.specialty] ?? d.specialty}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-[1fr_320px]">
        <div>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="fse"><FileSignature className="h-4 w-4 mr-1" /> Feuille de Soins</TabsTrigger>
              <TabsTrigger value="ald"><HeartHandshake className="h-4 w-4 mr-1" /> ALD</TabsTrigger>
              <TabsTrigger value="meds"><Pill className="h-4 w-4 mr-1" /> Médicaments</TabsTrigger>
              <TabsTrigger value="dental"><Sparkles className="h-4 w-4 mr-1" /> Dentaire</TabsTrigger>
            </TabsList>

            <TabsContent value="fse" className="mt-4">
              <FSEPanel doctorId={selectedDoctorId} specialty={selectedDoctor.specialty} allowedActs={allowedActs} />
            </TabsContent>

            <TabsContent value="ald" className="mt-4">
              <ALDPanel doctorId={selectedDoctorId} />
            </TabsContent>

            <TabsContent value="meds" className="mt-4">
              <MedsPanel specialty={selectedDoctor.specialty} />
            </TabsContent>

            <TabsContent value="dental" className="mt-4">
              <DentalPanel />
            </TabsContent>
          </Tabs>
        </div>

        {/* Right sidebar — Médecin signataire + Validation NGAP */}
        <DoctorSidebar doctorId={selectedDoctorId} allowedActsCount={allowedActs.length} />
      </div>
    </div>
  );
}

function FSEPanel({ doctorId, specialty, allowedActs }: { doctorId: string; specialty: string; allowedActs: NgapAct[] }) {
  const [patient, setPatient] = useState({ name: "James Miamai", nAffiliation: "AF-2025-0142", nImmat: "1024778", cnie: "JC125478", address: "Marrakech, Maroc" });
  const [insurance, setInsurance] = useState<"CNOPS/CNSS" | "PRIVE">("CNOPS/CNSS");
  const [link, setLink] = useState<"ASSURE" | "CONJOINT" | "ENFANT">("ASSURE");
  // `valeur` and `montant` are null when the act has no established amount:
  // null means "not established", and 0 would mean "free".
  const [acts, setActs] = useState<Array<{ id: string; date: string; code: string; lettreCle: string; coefficient: number; valeur: number | null; montant: number | null }>>([
    { id: "1", date: "06/19/2026", code: "C", lettreCle: "C", coefficient: 1, valeur: 150, montant: 150 },
  ]);

  const allowedCodes = new Set(allowedActs.map((a) => a.code));

  function addAct(act: any) {
    if (!allowedCodes.has(act.code)) return;
    const newId = String(acts.length + 1);
    // Un importe desconocido no es 0: es null, y null no se puede sumar.
    const valeur = act.baseAmountMad === null ? null : (act.baseAmountMad * act.coefficient) / 100;
    setActs((a) => [...a, { id: newId, date: new Date().toISOString().slice(0, 10), code: act.code, lettreCle: act.lettrage, coefficient: act.coefficient, valeur, montant: valeur }]);
  }

  function removeAct(id: string) {
    setActs((a) => a.filter((x) => x.id !== id));
  }

  const total = acts.reduce((s, a) => s + (a.montant ?? 0), 0);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Assuré & Bénéficiaire</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="text-xs text-muted-foreground uppercase">Nom & prénom de l'assuré</label>
              <Input value={patient.name} onChange={(e) => setPatient({ ...patient, name: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase">N° d'affiliation</label>
              <Input value={patient.nAffiliation} onChange={(e) => setPatient({ ...patient, nAffiliation: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase">N° d'immatriculation</label>
              <Input value={patient.nImmat} onChange={(e) => setPatient({ ...patient, nImmat: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase">CNIE</label>
              <Input value={patient.cnie} onChange={(e) => setPatient({ ...patient, cnie: e.target.value })} className="mt-1" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground uppercase">Adresse</label>
              <Input value={patient.address} onChange={(e) => setPatient({ ...patient, address: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase">Lien</label>
              <Select value={link} onValueChange={(v) => setLink(v as any)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ASSURE">Assuré</SelectItem>
                  <SelectItem value="CONJOINT">Conjoint</SelectItem>
                  <SelectItem value="ENFANT">Enfant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase">N° d'affiliation</label>
              <Input value={patient.nAffiliation} onChange={(e) => setPatient({ ...patient, nAffiliation: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase">Date naissance</label>
              <Input type="date" defaultValue="1989-12-04" className="mt-1" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase">Sexe</label>
              <Select defaultValue="M">
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">M</SelectItem>
                  <SelectItem value="F">F</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Actes & Cotation NGAP</CardTitle>
              <CardDescription>
                {allowedActs.length} actes autorisés pour votre profil ({SPECIALTY_LABEL[specialty]})
              </CardDescription>
            </div>
            <Select onValueChange={(code) => {
              const act = allowedActs.find((a) => a.code === code);
              if (act) addAct(act);
            }}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder="+ Ajouter un acte" />
              </SelectTrigger>
              <SelectContent>
                {allowedActs.map((a) => (
                  <SelectItem key={a.code} value={a.code}>
                    {a.code} · {a.label} · {formatAmountMad(a.baseAmountMad === null ? null : (a.baseAmountMad * a.coefficient) / 100)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_auto] gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
            <div>Date</div>
            <div>Code</div>
            <div>Lettre clé</div>
            <div>Cotation</div>
            <div>Valeur</div>
            <div>Montant (MAD)</div>
            <div></div>
          </div>
          <div className="space-y-2">
            {acts.map((a) => (
              <div key={a.id} className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_auto] gap-2">
                <Input type="date" value={a.date} onChange={(e) => setActs((acts) => acts.map((x) => x.id === a.id ? { ...x, date: e.target.value } : x))} />
                <Input value={a.code} onChange={(e) => setActs((acts) => acts.map((x) => x.id === a.id ? { ...x, code: e.target.value } : x))} className="font-mono" />
                <Input value={a.lettreCle} onChange={(e) => setActs((acts) => acts.map((x) => x.id === a.id ? { ...x, lettreCle: e.target.value } : x))} className="font-mono" />
                <Input type="number" value={a.coefficient} onChange={(e) => setActs((acts) => acts.map((x) => x.id === a.id ? { ...x, coefficient: Number(e.target.value), valeur: (Number(e.target.value) * 150), montant: (Number(e.target.value) * 150) } : x))} />
                <Input type="number" value={a.valeur ?? ""} onChange={(e) => setActs((acts) => acts.map((x) => x.id === a.id ? { ...x, valeur: Number(e.target.value), montant: Number(e.target.value) * a.coefficient } : x))} />
                <Input type="number" value={a.montant ?? ""} onChange={(e) => setActs((acts) => acts.map((x) => x.id === a.id ? { ...x, montant: Number(e.target.value) } : x))} className="font-bold" />
                <Button size="icon" variant="ghost" onClick={() => removeAct(a.id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 flex items-center justify-between">
          <div>
            <div className="text-xs text-muted-foreground uppercase">Total à facturer</div>
            <div className="text-3xl font-bold">{total.toFixed(2)} <span className="text-sm text-muted-foreground">MAD</span></div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm"><Printer className="h-3.5 w-3.5 mr-1" /> Imprimer</Button>
            <Button variant="outline" size="sm"><FileText className="h-3.5 w-3.5 mr-1" /> PDF</Button>
            <Button size="sm"><Send className="h-3.5 w-3.5 mr-1" /> Soumettre {insurance}</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ALDPanel({ doctorId }: { doctorId: string }) {
  const [selectedAld, setSelectedAld] = useState<string | null>(null);
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Demande ALD</CardTitle>
          <CardDescription>Sélectionnez l'ALD pour laquelle le patient est reconnu</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2 max-h-96 overflow-y-auto">
            {ALD_ENTRIES.map((ald) => (
              <div
                key={ald.code}
                onClick={() => setSelectedAld(ald.code)}
                className={`rounded-md border p-2 cursor-pointer transition ${
                  selectedAld === ald.code ? "border-primary bg-primary/10" : "border-white/5 bg-white/[0.02] hover:bg-white/[0.05]"
                }`}
              >
                <div className="flex items-center gap-1">
                  <code className="font-mono text-xs font-bold">{ald.code}</code>
                  <Badge variant="secondary" className="text-[9px]">{ald.category}</Badge>
                </div>
                <div className="text-xs mt-1">{ald.label}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      {selectedAld && (
        <Card>
          <CardHeader>
            <CardTitle>Justification médicale</CardTitle>
            <CardDescription>Champ libre pour le médecin</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea placeholder="Décrivez le contexte clinique justifiant la reconnaissance ALD..." rows={5} />
            <div className="mt-3 flex items-center gap-2">
              <Button><Save className="h-3.5 w-3.5 mr-1" /> Enregistrer brouillon</Button>
              <Button variant="outline"><Send className="h-3.5 w-3.5 mr-1" /> Soumettre à l'organisme</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function MedsPanel({ specialty }: { specialty: string }) {
  const allowedMeds = getMedicationsForSpecialty(specialty);
  const [selected, setSelected] = useState<Array<{ id: string; brandName: string; form: string; qty: number; ppv: number; total: number }>>([
    { id: "1", brandName: "Amlor 5mg", form: "Comprimé", qty: 30, ppv: 78.5, total: 2355.0 },
  ]);
  const [search, setSearch] = useState("");

  const filtered = allowedMeds.filter((m) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return m.inn.toLowerCase().includes(s) || m.brandNames.some((b) => b.toLowerCase().includes(s));
  });

  function addMed(med: any) {
    const ppv = PPV_TABLE.find((p) => p.inn === med.inn && p.dosage === med.dosage);
    if (!ppv) return;
    setSelected((s) => [...s, { id: String(s.length + 1), brandName: `${ppv.brandName} ${ppv.dosage}`, form: ppv.form, qty: 30, ppv: ppv.ppvMAD, total: ppv.ppvMAD * 30 }]);
  }

  function removeMed(id: string) {
    setSelected((s) => s.filter((m) => m.id !== id));
  }

  const total = selected.reduce((s, m) => s + m.total, 0);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Sélection médicaments (catalogue Maroc)</CardTitle>
          <CardDescription>
            {allowedMeds.length} médicaments · Spécialité {SPECIALTY_LABEL[specialty] ?? specialty}
            <br />
            <span className="text-[11px]">PPV = Prix Public de Vente (base ANAM Maroc)</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Input
            placeholder="Rechercher (DCI, marque)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-3"
          />
          <div className="grid grid-cols-2 gap-1.5 max-h-48 overflow-y-auto">
            {filtered.slice(0, 12).map((m) => {
              const ppv = PPV_TABLE.find((p) => p.inn === m.inn && p.dosage === m.dosage);
              return (
                <div
                  key={m.id}
                  onClick={() => ppv && addMed(m)}
                  className="flex items-center gap-2 rounded-md border border-white/5 bg-white/[0.02] p-2 text-xs cursor-pointer hover:bg-white/[0.05]"
                >
                  <Pill className="h-3 w-3 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{m.inn} {m.dosage}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{m.brandNames[0]} · {m.form}</div>
                  </div>
                  {ppv && <span className="text-[11px] font-mono">{ppv.ppvMAD.toFixed(2)}</span>}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Médicaments prescrits</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_auto] gap-2 text-xs font-medium text-muted-foreground uppercase mb-2">
            <div>Nom commercial</div>
            <div>Forme</div>
            <div>Qté</div>
            <div>PPV (MAD)</div>
            <div>Total</div>
            <div></div>
          </div>
          <div className="space-y-2">
            {selected.map((m) => (
              <div key={m.id} className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_auto] gap-2">
                <Input value={m.brandName} onChange={(e) => setSelected((s) => s.map((x) => x.id === m.id ? { ...x, brandName: e.target.value } : x))} />
                <Input value={m.form} onChange={(e) => setSelected((s) => s.map((x) => x.id === m.id ? { ...x, form: e.target.value } : x))} />
                <Input type="number" value={m.qty} onChange={(e) => setSelected((s) => s.map((x) => x.id === m.id ? { ...x, qty: Number(e.target.value), total: m.ppv * Number(e.target.value) } : x))} />
                <Input type="number" step="0.01" value={m.ppv} onChange={(e) => setSelected((s) => s.map((x) => x.id === m.id ? { ...x, ppv: Number(e.target.value), total: Number(e.target.value) * x.qty } : x))} />
                <Input type="number" step="0.01" value={m.total} onChange={(e) => setSelected((s) => s.map((x) => x.id === m.id ? { ...x, total: Number(e.target.value) } : x))} className="font-bold" />
                <Button size="icon" variant="ghost" onClick={() => removeMed(m.id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 flex items-center justify-between">
          <div>
            <div className="text-xs text-muted-foreground uppercase">Total à facturer</div>
            <div className="text-3xl font-bold">{total.toFixed(2)} <span className="text-sm text-muted-foreground">MAD</span></div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm"><Printer className="h-3.5 w-3.5 mr-1" /> Imprimer</Button>
            <Button variant="outline" size="sm"><FileText className="h-3.5 w-3.5 mr-1" /> PDF</Button>
            <Button size="sm"><Send className="h-3.5 w-3.5 mr-1" /> Soumettre CNOPS</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function DentalPanel() {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Actes dentaires (CCAM / NGAP)</CardTitle>
          <CardDescription>Sélection de la nomenclature dentaire spécifique</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-dashed border-white/10 p-6 text-center text-sm text-muted-foreground">
            Module dentaire en cours de migration — pour l'instant, utilisez Feuille de Soins avec les codes Z (radio dentaire) ou C (consultation).
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function DoctorSidebar({ doctorId, allowedActsCount }: { doctorId: string; allowedActsCount: number }) {
  const doctor = MOCK_DOCTORS.find((d) => d.id === doctorId)!;
  const cotation = getDoctorCotation(doctorId);
  return (
    <div className="space-y-3">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Médecin signataire</CardTitle>
            <Badge variant="secondary" className="text-[10px]"><CheckCircle2 className="h-3 w-3 mr-0.5" /> Auto-chargé</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-full bg-accent/20 text-accent font-bold">
              AB
            </div>
            <div>
              <div className="font-medium text-sm">{doctor.name}</div>
              <div className="text-[10px] text-muted-foreground">{SPECIALTY_LABEL[doctor.specialty]}</div>
            </div>
          </div>
          <div className="border-t border-white/5 pt-2 space-y-1.5 text-xs">
            <div className="flex items-center gap-2">
              <Hash className="h-3 w-3 text-muted-foreground" />
              <span className="text-muted-foreground">INPE:</span>
              <span className="font-mono">{doctor.inpe}</span>
            </div>
            <div className="flex items-center gap-2">
              <Hash className="h-3 w-3 text-muted-foreground" />
              <span className="text-muted-foreground">Autorisation:</span>
              <span className="font-mono">12{doctor.inpe.substring(2)}/12</span>
            </div>
            <div className="flex items-center gap-2">
              <Building2 className="h-3 w-3 text-muted-foreground" />
              <span className="text-muted-foreground">Cabinet:</span>
              <span className="text-[11px]">{doctor.cabinet}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-3 w-3 text-muted-foreground" />
              <span className="text-muted-foreground">Ville:</span>
              <span>{doctor.city}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Statut:</span>
              <Badge variant="secondary"><CheckCircle2 className="h-3 w-3 mr-0.5" /> Actif</Badge>
            </div>
          </div>
          <div className="border-t border-white/5 pt-2 text-[10px] text-muted-foreground leading-relaxed">
            Modifiez ces paramètres dans <span className="underline">Configuration</span>. La signature et le cachet sont injectés automatiquement à la soumission.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-1.5">
            <Settings className="h-3.5 w-3.5" /> Validation NGAP
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1.5 text-xs">
          <ValidationRow label="Cotation conforme" ok={true} />
          <ValidationRow label="Bénéficiaire" ok={true} />
          <ValidationRow label="Signature praticien" ok={true} />
          <ValidationRow label="Codes CIM-10" ok={false} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Cotation médecin</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-emerald-400">{allowedActsCount}</div>
          <div className="text-xs text-muted-foreground">actes NGAP autorisés</div>
          <Button variant="outline" size="sm" className="mt-2 w-full" asChild>
            <Link to="/admin/doctor-cotations">Gérer (superadmin)</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function ValidationRow({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      {ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> : <span className="text-muted-foreground text-xs">—</span>}
    </div>
  );
}
